import React, { useEffect, useRef, useState } from 'react';
import { default as Cropper } from 'cropperjs';
import { Button, Modal } from '../../basic';
import { intl } from '../../../lib';

/**
 * Props for a {@link CropperModal}.
 */
export type CropperModalProps = {
  /**
   * The image that should be cropped.
   * This needs to be a valid value for an `<img>` `src` value.
   */
  image: string;
  /**
   * Callback for when the crop is saved.
   * @param croppedImage the cropped image src
   */
  onSave: (croppedImage: string) => void;
  /**
   * Callback for when the crop is cancelled.
   */
  onCancel: () => void;
};

/**
 * A Modal element for cropping an image.
 * This expects to be rendered only when the image is present and has a valid src, so the
 * `onSave` and `onCancel` callbacks also need to handle the hiding of the modal.
 *
 * @param props {@link CropperModalProps}
 * @return the created {@link JSX.Element}.
 * @constructor
 */
export function CropperModal({
  image,
  onSave,
  onCancel
}: Readonly<CropperModalProps>): JSX.Element {
  // cropper state
  const cropImgRef = useRef<HTMLImageElement>(null);
  const [cropper, setCropper] = useState<Cropper | undefined>(undefined);
  // state to help with modal sizing
  const sizingImageRef = useRef<HTMLImageElement>(null);
  const [wrapperSizing, setWrapperSizing] = useState<{
    width: number;
    height: number;
  } | null>(null);

  useEffect(() => {
    if (cropImgRef.current && wrapperSizing) {
      if (cropper) {
        cropper.destroy();
      }

      setCropper(
        new Cropper(cropImgRef.current, {
          viewMode: 2,
          aspectRatio: 1,
          autoCropArea: 1,
          modal: true,
          minCropBoxWidth: 50,
          minCropBoxHeight: 50
        })
      );
    }
    // the cropper should only be initialized once for the component
    // and it definitely shouldn't be part of the dependency list
    // therefore, disabling react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wrapperSizing]);

  // effect to destroy the cropper when the component is unmounted
  useEffect(() => {
    return () => {
      if (cropper) {
        cropper?.destroy();
        setCropper(undefined);
      }
    };
  }, [cropper]);

  return (
    <>
      {!wrapperSizing && (
        // hidden image to determine the size of the image.
        // this is needed so that the modal can size and position itself properly without the need of
        // something like an imperative hook on the Modal to call something along the lines of "repositionModal"
        <img
          ref={sizingImageRef}
          src={image}
          style={{ display: 'none' }}
          aria-hidden={true}
          alt="crop-image.png"
          onLoad={() => {
            const image = sizingImageRef.current;
            if (image) {
              const naturalWidth = image.naturalWidth;
              const naturalHeight = image.naturalHeight;
              const max = 500;
              if (naturalWidth >= naturalHeight) {
                setWrapperSizing({
                  width: max,
                  height: (naturalHeight * max) / naturalWidth
                });
              } else {
                setWrapperSizing({
                  width: (naturalWidth * max) / naturalHeight,
                  height: max
                });
              }
            }
          }}
        />
      )}

      {wrapperSizing && (
        <Modal
          className="ehs-cropper-modal"
          draggable
          // TODO: use common key
          title={intl('patientpersonaldataportlet.view.edit.image')}
          actions={
            <>
              <Button
                variant="primary"
                text={intl('view.button.save')}
                onClick={() => {
                  if (cropper) {
                    onSave(cropper.getCroppedCanvas().toDataURL());
                  }
                }}
              />
              <Button
                variant="gray"
                text={intl('view.button.cancel')}
                onClick={onCancel}
              />
            </>
          }
        >
          <div className="cropper-modal-content" style={{ ...wrapperSizing }}>
            <img
              ref={cropImgRef}
              src={image}
              alt="crop-image.png"
              style={{
                maxWidth: 500,
                maxHeight: 500
              }}
            />
          </div>
        </Modal>
      )}
    </>
  );
}
